import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Phone, Mail, Plus, Trash2, Loader2, Clock, Users2, AlertTriangle, Shield, Calendar, UserCheck, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const DAY_SHORT = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

let keySeq = 0;
const newKey = () => `row-${++keySeq}`;

function CurrentOnCall({ current }) {
  if (!current?.length) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <AlertTriangle className="h-4 w-4 mr-2" />Nenhum plantonista ativo no momento
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {current.map((c) => (
        <Card key={`${c.user_id || c.user_email}-${c.start_time}`} className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="h-4 w-4 text-emerald-600" />
              <span className="text-sm font-semibold">{c.user_name}</span>
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p><Mail className="inline h-3 w-3 mr-1" />{c.user_email}</p>
              <p><Phone className="inline h-3 w-3 mr-1" />{c.phone || 'Não cadastrado'}</p>
              <p><Clock className="inline h-3 w-3 mr-1" />{c.start_time} - {c.end_time}</p>
              {c.group_name && <Badge variant="secondary" className="text-xs mt-1">{c.group_name}</Badge>}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function WeekView({ entries }) {
  return (
    <div className="grid grid-cols-7 gap-1">
      {DAYS.map((day, i) => {
        const dayEntries = entries.filter(e => e.day_of_week === i);
        return (
          <div key={day} className="border rounded-lg p-2 min-h-[80px]">
            <p className="text-xs font-semibold text-center mb-1 text-muted-foreground">{DAY_SHORT[i]}</p>
            {dayEntries.length > 0 ? dayEntries.map((e) => (
              <div key={`${e.user_id}-${e.start_time}-${e.end_time}`} className="bg-primary/10 rounded p-1.5 text-xs mb-1">
                <p className="font-medium truncate">{e.user_name || 'Usuário'}</p>
                <p className="text-muted-foreground">{e.start_time}-{e.end_time}</p>
              </div>
            )) : (
              <p className="text-xs text-muted-foreground text-center mt-2">-</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function OnCallPage() {
  const [schedules, setSchedules] = useState([]);
  const [currentOnCall, setCurrentOnCall] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editSchedule, setEditSchedule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', group_id: '', rotation_type: 'weekly', is_active: true,
    entries: [], escalation: [],
  });
  const { getAuthHeader } = useAuthStore();

  const fetchData = useCallback(async () => {
    try {
      const [schedRes, currentRes, groupsRes, usersRes] = await Promise.all([
        axios.get(`${API}/oncall`, { headers: getAuthHeader() }),
        axios.get(`${API}/oncall/current`, { headers: getAuthHeader() }),
        axios.get(`${API}/groups`, { headers: getAuthHeader() }),
        axios.get(`${API}/users`, { headers: getAuthHeader() }),
      ]);
      setSchedules(schedRes.data);
      setCurrentOnCall(currentRes.data.current || []);
      setGroups(groupsRes.data);
      setUsers(usersRes.data.filter(u => u.is_active));
    } catch (err) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addEntry = () => {
    setForm({ ...form, entries: [...form.entries, { _k: newKey(), day_of_week: 0, user_id: '', start_time: '08:00', end_time: '18:00' }] });
  };

  const updateEntry = (idx, field, value) => {
    const entries = [...form.entries];
    entries[idx] = { ...entries[idx], [field]: field === 'day_of_week' ? parseInt(value) : value };
    setForm({ ...form, entries });
  };

  const removeEntry = (idx) => {
    setForm({ ...form, entries: form.entries.filter((_, i) => i !== idx) });
  };

  const addEscalation = () => {
    const nextLevel = form.escalation.length + 1;
    setForm({ ...form, escalation: [...form.escalation, { _k: newKey(), level: nextLevel, user_id: '', phone: '', email: '', wait_minutes: 15 }] });
  };

  const updateEscalation = (idx, field, value) => {
    const escalation = [...form.escalation];
    escalation[idx] = { ...escalation[idx], [field]: field === 'wait_minutes' || field === 'level' ? parseInt(value) || 0 : value };
    // Auto-fill email and phone when selecting user
    if (field === 'user_id') {
      const selectedUser = users.find(u => u.id === value);
      if (selectedUser) {
        escalation[idx].email = selectedUser.email || '';
        escalation[idx].phone = selectedUser.phone || '';
      }
    }
    setForm({ ...form, escalation });
  };

  const removeEscalation = (idx) => {
    setForm({ ...form, escalation: form.escalation.filter((_, i) => i !== idx).map((e, i) => ({ ...e, level: i + 1 })) });
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        group_id: form.group_id || null,
        entries: form.entries.map(({ _k, ...e }) => e),
        escalation: form.escalation.map(({ _k, ...e }) => e),
      };
      if (editSchedule) {
        await axios.put(`${API}/oncall/${editSchedule.id}`, payload, { headers: getAuthHeader() });
        toast.success('Escala atualizada');
      } else {
        await axios.post(`${API}/oncall`, payload, { headers: getAuthHeader() });
        toast.success('Escala criada');
      }
      setShowCreate(false);
      setEditSchedule(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta escala?')) return;
    try {
      await axios.delete(`${API}/oncall/${id}`, { headers: getAuthHeader() });
      toast.success('Escala excluída');
      fetchData();
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const openEdit = (s) => {
    setEditSchedule(s);
    setForm({
      name: s.name, group_id: s.group_id || '', rotation_type: s.rotation_type || 'weekly',
      is_active: s.is_active !== false,
      entries: (s.entries || []).map(e => ({ _k: newKey(), day_of_week: e.day_of_week, user_id: e.user_id, start_time: e.start_time || '08:00', end_time: e.end_time || '18:00' })),
      escalation: (s.escalation || []).map(e => ({ _k: newKey(), level: e.level, user_id: e.user_id, phone: e.phone || '', email: e.email || '', wait_minutes: e.wait_minutes || 15 })),
    });
    setShowCreate(true);
  };

  const openCreate = () => {
    setEditSchedule(null);
    setForm({ name: '', group_id: '', rotation_type: 'weekly', is_active: true, entries: [], escalation: [] });
    setShowCreate(true);
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-40" /><Skeleton className="h-[200px] w-full" /><Skeleton className="h-[300px] w-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="oncall-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Gestão de Plantão (OnCall)</h2>
          <p className="text-sm text-muted-foreground">Escalas, acionamentos e contatos de plantão</p>
        </div>
        <Button onClick={openCreate} data-testid="create-schedule-btn">
          <Plus className="h-4 w-4 mr-2" />Nova Escala
        </Button>
      </div>

      {/* Current On-Call */}
      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
          <Shield className="h-4 w-4" />Plantonistas Ativos Agora
        </h3>
        <CurrentOnCall current={currentOnCall} />
      </div>

      {/* Schedules */}
      {schedules.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">Nenhuma escala criada</p>
            <Button variant="outline" className="mt-4" onClick={openCreate}>Criar primeira escala</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {schedules.map(s => (
            <Card key={s.id} data-testid={`schedule-card-${s.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">{s.name}</CardTitle>
                    {s.group_name && (
                      <Badge style={{ backgroundColor: s.group_color, color: '#fff' }} className="text-xs">{s.group_name}</Badge>
                    )}
                    <Badge variant={s.is_active ? 'default' : 'secondary'} className="text-xs">
                      {s.is_active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(s)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(s.id)}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="week">
                  <TabsList>
                    <TabsTrigger value="week">Semana</TabsTrigger>
                    <TabsTrigger value="escalation">Escalação ({s.escalation?.length || 0})</TabsTrigger>
                  </TabsList>
                  <TabsContent value="week" className="mt-3">
                    <WeekView entries={s.entries || []} />
                  </TabsContent>
                  <TabsContent value="escalation" className="mt-3">
                    {s.escalation?.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">Nível</TableHead>
                            <TableHead>Contato</TableHead>
                            <TableHead>Telefone</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Aguardar</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {s.escalation.map((esc) => (
                            <TableRow key={`${esc.level}-${esc.user_id || esc.email}`}>
                              <TableCell><Badge variant="outline">N{esc.level}</Badge></TableCell>
                              <TableCell className="font-medium">{esc.user_name || '-'}</TableCell>
                              <TableCell className="text-sm">{esc.phone || '-'}</TableCell>
                              <TableCell className="text-sm">{esc.email || '-'}</TableCell>
                              <TableCell className="text-sm">{esc.wait_minutes}min</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">Nenhuma escalação configurada</p>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreate} onOpenChange={(v) => { if (!v) { setShowCreate(false); setEditSchedule(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editSchedule ? 'Editar Escala' : 'Nova Escala de Plantão'}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-6 pr-2">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome da Escala</Label>
                <Input placeholder="Ex: Plantão NOC" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="schedule-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Grupo/Time</Label>
                <Select value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Selecionar grupo" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Escala ativa</Label>
            </div>

            {/* Weekly Entries */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Escala Semanal</Label>
                <Button variant="outline" size="sm" onClick={addEntry}><Plus className="h-3 w-3 mr-1" />Adicionar</Button>
              </div>
              {form.entries.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-3 border rounded-lg">Nenhum turno definido</p>
              ) : (
                <div className="space-y-2">
                  {form.entries.map((entry, i) => (
                    <div key={entry._k} className="flex items-center gap-2 p-2 border rounded-lg">
                      <Select value={String(entry.day_of_week)} onValueChange={(v) => updateEntry(i, 'day_of_week', v)}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>{DAYS.map((d, di) => <SelectItem key={d} value={String(di)}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={entry.user_id} onValueChange={(v) => updateEntry(i, 'user_id', v)}>
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Usuário" /></SelectTrigger>
                        <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="w-20" type="time" value={entry.start_time} onChange={(e) => updateEntry(i, 'start_time', e.target.value)} />
                      <span className="text-muted-foreground">-</span>
                      <Input className="w-20" type="time" value={entry.end_time} onChange={(e) => updateEntry(i, 'end_time', e.target.value)} />
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeEntry(i)}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Escalation */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Escalação de Acionamento</Label>
                <Button variant="outline" size="sm" onClick={addEscalation}><Plus className="h-3 w-3 mr-1" />Nível</Button>
              </div>
              {form.escalation.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-3 border rounded-lg">Nenhuma escalação definida</p>
              ) : (
                <div className="space-y-2">
                  {form.escalation.map((esc, i) => (
                    <div key={esc._k} className="p-3 border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">Nível {esc.level}</Badge>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeEscalation(i)}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Select value={esc.user_id} onValueChange={(v) => updateEscalation(i, 'user_id', v)}>
                          <SelectTrigger><SelectValue placeholder="Contato" /></SelectTrigger>
                          <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="Telefone" value={esc.phone} onChange={(e) => updateEscalation(i, 'phone', e.target.value)} />
                        <Input placeholder="Email" value={esc.email} onChange={(e) => updateEscalation(i, 'email', e.target.value)} />
                        <div className="flex items-center gap-2">
                          <Input type="number" className="w-20" min={1} max={120} value={esc.wait_minutes} onChange={(e) => updateEscalation(i, 'wait_minutes', e.target.value)} />
                          <span className="text-xs text-muted-foreground">min antes de escalar</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="shrink-0 pt-4">
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditSchedule(null); }}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editSchedule ? 'Salvar' : 'Criar Escala'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
